import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useCreateBriefLedger } from "@entities/brief/lib/hooks/useCreateBriefLedger";
import { useEditBriefLedger } from "@entities/brief/lib/hooks/useEditBriefLedger";
import { useGetBriefLedgerAggregation } from "@entities/brief/lib/hooks/useGetBriefLedgerAggregation";
import { useGetBriefLedgerFacets } from "@entities/brief/lib/hooks/useGetBriefLedgerFacets";
import { useGetBriefLedgers } from "@entities/brief/lib/hooks/useGetBriefLedgers";
import { InfoIcon } from "@phosphor-icons/react/dist/ssr";
import { ToastService } from "@shared/lib/services";
import {
  IBriefLedger,
  IBriefLedgerSearchRequest,
  IEditBriefLedger,
  IGetBriefLedgerAggregation,
  LEDGER_TYPE,
} from "@shared/lib/types/briefLedger";
import { formatData } from "@shared/lib/utils/formatData";
import { Table, TableTitleLayout } from "@shared/ui";
import { Button } from "@shared/ui/Button";
import SelectFilter from "@shared/ui/filters/SelectFilter";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import {
  Card,
  Col,
  Divider,
  Flex,
  Input,
  Modal,
  Row,
  Space,
  Tag,
  Tooltip,
  Typography
} from "antd";
import { ColumnsType } from "antd/es/table";
import { Search } from "lucide-react";
import { useEffect, useState } from "react";
import { CreateBriefLedgerEntry } from "./CreateBriefLedgerEntry";
import { EditBriefLedgerEntry } from "./EditBriefLedgerEntry";

const { Title } = Typography;

export function BriefLedger({ briefId }: { briefId: string }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [tableType, setTableType] = useState<LEDGER_TYPE[]>([]);
  const [tableSubType, setTableSubType] = useState<string[]>([]);
  const [billingIds, setBillingIds] = useState<string[]>([]);
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [selectedLedger, setSelectedLedger] = useState<IBriefLedger | null>(
    null
  );

  const searchParams: IBriefLedgerSearchRequest = {
    briefId,
    searchQuery,
    type: tableType,
    subType: tableSubType,
  };

  const { ledgers, isFetching, totalCount } = useGetBriefLedgers(searchParams);
  const { facets } = useGetBriefLedgerFacets(briefId);
  const { create, isCreating, isCreated } = useCreateBriefLedger();
  const { edit, isEditing, isEdited } = useEditBriefLedger();

  const { aggregation } = useGetBriefLedgerAggregation(briefId);

  const typeToDisplay = facets?.type.map((item) => item._id);
  const subTypeToDisplay = facets?.subType.map((item) => item._id);
  const billingIdsToDisplay = facets?.billing.map((item) => ({
    name: item.name,
    value: item._id,
  }));

  useEffect(() => {
    if (isCreated) {
      setCreateModalOpen(false);
      ToastService.success("Ledger entry created successfully");
    }
  }, [isCreated]);

  useEffect(() => {
    if (isEdited) {
      setEditModalOpen(false);
      setSelectedLedger(null);
      ToastService.success("Ledger entry updated successfully");
    }
  }, [isEdited]);

  const handleCreate = (values: any) => {
    create({
      ...values,
      briefId,
    });
  };

  const handleEdit = (values: IEditBriefLedger) => {
    edit(values);
  };

  const handleEditClick = (record: IBriefLedger) => {
    setSelectedLedger(record);
    setEditModalOpen(true);
  };

  const columns: ColumnsType<IBriefLedger> = [
    {
      title: "Influencer",
      key: "influencer",
      render: (_: string, record: IBriefLedger) => (
        <Flex vertical>
          <div>{record?.influencerFullName || "N/A"}</div>
          <span>{record?.socialMediaName || "N/A"}</span>
        </Flex>
      ),
    },
    {
      title: "Remarks",
      key: "remarks",
      render: (_: string, record: IBriefLedger) => (
        <TextWithTooltip text={record?.remarks || ""} />
      ),
    },
    {
      title: "Billing",
      key: "billing",
      filterCount: billingIds.length,
      filterDropdownProps: { destroyPopupOnHide: true },
      filtered: !!billingIds.length,
      render: (_: string, record: IBriefLedger) => (
        <div>{record.billing.name || "N/A"}</div>
      ),
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SelectFilter
          title="Billing"
          props={props}
          placeholder="Select Billing"
          initialTagCount={3}
          itemsToDisplay={billingIdsToDisplay}
          selectedItems={billingIds}
          handleSelectionChange={(items: string[]) => setBillingIds(items)}
        />
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      filterCount: tableType.length,
      filterDropdownProps: { destroyPopupOnHide: true },
      filtered: !!tableType.length,
      render: (type: LEDGER_TYPE) =>
        type === LEDGER_TYPE.Credit ? (
          <Tag color="red">{type}</Tag>
        ) : (
          <Tag color="green">{type}</Tag>
        ),
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SelectFilter
          title="Type"
          props={props}
          placeholder="Select Type"
          initialTagCount={3}
          itemsToDisplay={typeToDisplay}
          selectedItems={tableType}
          handleSelectionChange={(items: string[]) =>
            setTableType(items as LEDGER_TYPE[])
          }
        />
      ),
    },
    {
      title: "Sub Type",
      dataIndex: "subType",
      key: "subType",
      filterCount: tableSubType.length,
      filterDropdownProps: { destroyPopupOnHide: true },
      filtered: !!tableSubType.length,
      render: (subType: string) => (
        <Tag
          color={
            subType === "Others"
              ? "geekblue"
              : subType === "Influencer-Purchased"
              ? "green"
              : "red"
          }
        >
          {subType}
        </Tag>
      ),
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SelectFilter
          title="Sub Type"
          props={props}
          placeholder="Select Sub Type"
          initialTagCount={3}
          itemsToDisplay={subTypeToDisplay}
          selectedItems={tableSubType}
          handleSelectionChange={(items: string[]) => setTableSubType(items)}
        />
      ),
    },
    {
      title: "Selling Cost",
      key: "sellingCost",
      render: (_: string, record: IBriefLedger) => (
        <div>{formatData(record.sellingCost || 0)}</div>
      ),
    },
    {
      title: "Service Cost",
      key: "serviceCost",
      render: (_: string, record: IBriefLedger) => (
        <div>{formatData(record.serviceCost || 0)}</div>
      ),
    },
    {
      title: "Base Cost",
      key: "baseCost",
      render: (_: string, record: IBriefLedger) => (
        <div>{formatData(record.baseCost || 0)}</div>
      ),
    },
    {
      title: "gst Rate",
      key: "gstRate",
      render: (_: string, record: IBriefLedger) => (
        <div>{formatData(record.gstRate || 0)} %</div>
      ),
    },
    {
      title: "GST Amount",
      key: "gstAmount",
      render: (_: string, record: IBriefLedger) => (
        <div>{formatData(record.gstAmount || 0)}</div>
      ),
    },
    {
      title: "Total Amount",
      key: "totalAmount",
      render: (_: string, record: IBriefLedger) => (
        <div>{formatData(record.totalAmount || 0)}</div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: string, record: IBriefLedger) => (
        <>
          {record.subType === "Others" && (
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEditClick(record)}
              type="text"
            />
          )}
        </>
      ),
    },
  ];

  return (
    <>
        
        <Table
          columns={columns}
          title={() => <TableTitleLayout 
            filterComponent={<div style={{width: "100%"}}>
              <Flex justify="space-between" style={{width: "100%"}}>
                <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by  name or billing ID"
                            style={{width: 300}}

              prefix={<Search size={14}/>}
            />
            <Button
              icon={<PlusOutlined />}
              onClick={() => setCreateModalOpen(true)}
              type="primary"
            >
              Create Ledger
            </Button>
              </Flex>
              <Divider />
              <Row gutter={16}>
          {aggregation?.all && (
            <Col span={8}>
              <AggregationCard values={aggregation?.all} title="All" />
            </Col>
          )}
          {aggregation?.debits && (
            <Col span={8}>
              <AggregationCard values={aggregation?.debits} title="Debits" />
            </Col>
          )}
          {aggregation?.credits && (
            <Col span={8}>
              <AggregationCard values={aggregation?.credits} title="Credits" />
            </Col>
          )}
        </Row>
            </div>}
            totalText={`${totalCount || 0} Records found`}
          />}
          loading={isFetching}
          dataSource={ledgers}
          scroll={{ x: 1000 }}
        />

      <Modal
        title="Create Ledger Entry"
        open={isCreateModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
        }}
        footer={null}
        destroyOnClose
      >
        <CreateBriefLedgerEntry
          onSubmit={handleCreate}
          isSubmitting={isCreating}
          onCancel={() => setCreateModalOpen(false)}
        />
      </Modal>

      <Modal
        title="Edit Ledger Entry"
        open={isEditModalOpen}
        onCancel={() => {
          setEditModalOpen(false);
          setSelectedLedger(null);
        }}
        footer={null}
        destroyOnClose
      >
        {selectedLedger && (
          <EditBriefLedgerEntry
            onSubmit={handleEdit}
            isSubmitting={isEditing}
            onCancel={() => {
              setEditModalOpen(false);
              setSelectedLedger(null);
            }}
            initialValues={selectedLedger}
          />
        )}
      </Modal>
    </>
  );
}

export function AggregationCard({
  values,
  title,
}: {
  values: IGetBriefLedgerAggregation["debits"];
  title: string;
}) {
  return (
    <Card title={title} bordered={false}>
      <Flex vertical>
        <div>Total Amount : ₹{(values?.totalAmount || 0).toFixed(2)}</div>
        <Space align="center">
          <Tooltip title="Base Cost = Selling Cost + Service Cost">
            <InfoIcon size={12} />
          </Tooltip>
          <span>Base Amount : ₹{(values?.baseCost || 0).toFixed(2)}</span>
        </Space>
        <div>Gst Amount : ₹{(values?.gstAmount || 0).toFixed(2)}</div>
        <div>Selling Cost : ₹{(values?.sellingCost || 0).toFixed(2)}</div>
        <div>Service Cost : ₹{(values?.serviceCost || 0).toFixed(2)}</div>
      </Flex>
    </Card>
  );
}
